import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  normalizeConnectionEmbeddingsStatus,
  runConnectionEmbeddingIndex,
  scheduleConnectionEmbeddingIndex,
} from '../../lib/connectionEmbeddings';
import {
  ArrowLeft,
  Bell,
  CalendarClock,
  Check,
  Database,
  FileText,
  Linkedin,
  Loader2,
  Mail,
  Newspaper,
  Pencil,
  RefreshCw,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { normalizeConnectionEmail } from '../../lib/connectionEmail';
import { captureEdgeFunctionFailure, reportUiError } from '../../sentry';
import ConnectionAiResourceMock from './ConnectionAiResourceMock';
import ConnectionModal, { type Connection } from './ConnectionModal';
import { type CalendlyMeeting, calendlyMeetingStatusClass } from './CalendlyMeetingsModal';
import TranscriptViewerModal, { type TranscriptViewerLinkedConnection } from '../Transcripts/TranscriptViewerModal';
import EmailSummaryModal from './EmailSummaryModal';
import BeehiivSubscriberModal, { type BeehiivSubscriberModalDetail } from './BeehiivSubscriberModal';
import InviteToNewsletterModal from './InviteToNewsletterModal';

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

/** Calendar-day difference for "X days ago" next to the name. */
function lastContactDaysAgoLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const then = new Date(d);
  then.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - then.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return `in ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`;
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
}

type LinkedTranscriptRow = {
  id: string;
  title: string;
  content: string;
  summary: string | null;
  meeting_date: string;
  participants: string[] | null;
  transcript_chunks_status?: string | null;
};

/**
 * Transcripts where any `participants[]` value contains the email (case-insensitive substring).
 * Handles entries like `"Stephan Smith <stephan.smith.bc93@gmail.com>"` via DB function
 * `transcripts_for_participant_email` (substring match on each array element).
 */
async function fetchTranscriptsByParticipantEmail(email: string): Promise<LinkedTranscriptRow[]> {
  const trimmed = email.trim();
  if (!trimmed) return [];

  const { data, error } = await supabase.rpc('transcripts_for_participant_email', {
    p_email: trimmed,
  });

  if (error) throw error;

  return (data ?? []) as LinkedTranscriptRow[];
}

function transcriptPreview(row: LinkedTranscriptRow): string {
  const fromSummary = row.summary?.trim();
  if (fromSummary) return fromSummary;
  const c = row.content?.trim() ?? '';
  if (c.length <= 200) return c;
  return `${c.slice(0, 197)}…`;
}

function connectionEmbeddingsStatusLabel(status: string | null | undefined): string {
  const s = normalizeConnectionEmbeddingsStatus(status);
  switch (s) {
    case 'ready':
      return 'Indexed';
    case 'pending':
      return 'Indexing…';
    case 'error':
      return 'Index failed';
    default:
      return 'Not indexed';
  }
}

function transcriptChunksStatusLabel(status: string | null | undefined): string {
  switch (status ?? 'none') {
    case 'ready':
      return 'Ready';
    case 'pending':
      return 'Indexing…';
    case 'error':
      return 'Error';
    default:
      return 'Not indexed';
  }
}

function formatBeehiivUnixCreated(created: number): string {
  if (!created) return '—';
  try {
    return new Date(created * 1000).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(created);
  }
}

type BeehiivEmailLookupSubscription = {
  id: string;
  email: string;
  status: string;
  created: number;
  subscriptionTier: string;
  linkedUrl?: string;
};

type BeehiivEmailLookupField = { name: string; kind: string; value: unknown };

function calendlyStatusLabel(s: CalendlyMeeting['meetingStatus']): string {
  switch (s) {
    case 'active':
      return 'Scheduled';
    case 'completed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    default:
      return s;
  }
}

const ConnectionDetailView: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? Number.parseInt(idParam, 10) : NaN;

  const [connection, setConnection] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [aiResourceMockOpen, setAiResourceMockOpen] = useState(false);
  const [emailSummaryOpen, setEmailSummaryOpen] = useState(false);

  const [calendlyMeetings, setCalendlyMeetings] = useState<CalendlyMeeting[]>([]);
  const [calendlyLoading, setCalendlyLoading] = useState(false);
  const [calendlyError, setCalendlyError] = useState<string | null>(null);

  const [linkedTranscripts, setLinkedTranscripts] = useState<LinkedTranscriptRow[]>([]);
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptsError, setTranscriptsError] = useState<string | null>(null);
  const [viewerTranscript, setViewerTranscript] = useState<LinkedTranscriptRow | null>(null);

  const [emailInlineEdit, setEmailInlineEdit] = useState(false);
  const [emailInlineDraft, setEmailInlineDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [icpInlineEdit, setIcpInlineEdit] = useState(false);
  const [icpInlineDraft, setIcpInlineDraft] = useState('');
  const icpTaRef = useRef<HTMLTextAreaElement>(null);
  const skipIcpBlurSave = useRef(false);

  const [callWhenInlineN, setCallWhenInlineN] = useState<null | 1 | 2 | 3>(null);
  const [callWhenInlineDraft, setCallWhenInlineDraft] = useState('');
  const callWhenTaRef = useRef<HTMLTextAreaElement>(null);
  const skipCallWhenBlurSave = useRef(false);

  const [embeddingIndexLoading, setEmbeddingIndexLoading] = useState(false);

  const [beehiivStatus, setBeehiivStatus] = useState<
    'idle' | 'loading' | 'found' | 'not_found' | 'error' | 'no_email'
  >('idle');
  const [beehiivMatch, setBeehiivMatch] = useState<{
    subscription: BeehiivEmailLookupSubscription;
    tags: string[];
    customFields: BeehiivEmailLookupField[];
  } | null>(null);
  const [beehiivError, setBeehiivError] = useState<string | null>(null);
  /** Normalized address the edge function sent to beehiiv (helps debug “not found” vs wrong email). */
  const [beehiivLookupEmail, setBeehiivLookupEmail] = useState<string | null>(null);
  const [beehiivSubscriberModalOpen, setBeehiivSubscriberModalOpen] = useState(false);
  const [beehiivDetailMatch, setBeehiivDetailMatch] = useState<BeehiivSubscriberModalDetail | null>(null);
  const [beehiivDetailsLoading, setBeehiivDetailsLoading] = useState(false);
  const [beehiivDetailsError, setBeehiivDetailsError] = useState<string | null>(null);
  const [inviteNewsletterOpen, setInviteNewsletterOpen] = useState(false);
  /** Connection email for the in-flight beehiiv lookup; used to ignore stale detail responses after navigation. */
  const beehiivConnectionEmailRef = useRef<string>('');

  const loadBeehiivSubscriptionDetails = useCallback(async (subscriptionId: string) => {
    const emailSnapshot = beehiivConnectionEmailRef.current;
    if (!subscriptionId.trim()) return;
    setBeehiivDetailsLoading(true);
    setBeehiivDetailsError(null);
    setBeehiivDetailMatch(null);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        subscription?: BeehiivEmailLookupSubscription;
        tags?: string[];
        customFields?: BeehiivEmailLookupField[];
      }>('beehiiv-subscriptions', { body: { subscriptionId } });
      if (beehiivConnectionEmailRef.current !== emailSnapshot) return;
      if (error) {
        throw new Error(error.message || 'Beehiiv request failed');
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      if (!data?.subscription || typeof data.subscription !== 'object') {
        throw new Error('No subscription in response');
      }
      setBeehiivDetailMatch({
        subscription: data.subscription,
        tags: Array.isArray(data.tags) ? data.tags : [],
        customFields: Array.isArray(data.customFields) ? data.customFields : [],
      });
    } catch (e: unknown) {
      if (beehiivConnectionEmailRef.current !== emailSnapshot) return;
      captureEdgeFunctionFailure('beehiiv-subscriptions', 'get', e, {
        subscription_id: subscriptionId,
      });
      setBeehiivDetailsError(e instanceof Error ? e.message : 'Could not load beehiiv details');
    } finally {
      if (beehiivConnectionEmailRef.current === emailSnapshot) {
        setBeehiivDetailsLoading(false);
      }
    }
  }, []);

  const openBeehiivSubscriberModal = useCallback((subscriptionId: string) => {
    if (!subscriptionId.trim()) return;
    setBeehiivSubscriberModalOpen(true);
    void loadBeehiivSubscriptionDetails(subscriptionId);
  }, [loadBeehiivSubscriptionDetails]);

  const load = useCallback(async () => {
    if (Number.isNaN(id)) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setNotFound(false);
      const { data, error } = await supabase.from('connections').select('*').eq('id', id).maybeSingle();

      if (error) throw error;
      if (!data) {
        setConnection(null);
        setNotFound(true);
        return;
      }
      setConnection(data as Connection);
    } catch (e: unknown) {
      reportUiError('connections', 'load', e, { connection_id: String(id) });
      const message = e instanceof Error ? e.message : 'Failed to load connection';
      toast.error(message);
      setConnection(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setInviteNewsletterOpen(false);
  }, [connection?.id]);

  const loadCalendlyForConnection = useCallback(async (email: string | null | undefined) => {
    const trimmed = email?.trim() ?? '';
    if (!trimmed) {
      setCalendlyMeetings([]);
      setCalendlyError(null);
      setCalendlyLoading(false);
      return;
    }
    setCalendlyLoading(true);
    setCalendlyError(null);
    try {
      const { data, error } = await supabase.functions.invoke<{ meetings?: CalendlyMeeting[]; error?: string }>(
        'calendly-meetings',
        { body: { inviteeEmail: trimmed } }
      );
      if (error) {
        throw new Error(error.message || 'Failed to load Calendly meetings');
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      setCalendlyMeetings(Array.isArray(data?.meetings) ? data.meetings : []);
    } catch (e: unknown) {
      captureEdgeFunctionFailure('calendly-meetings', 'list', e, {
        invitee_email: trimmed,
      });
      const message = e instanceof Error ? e.message : 'Could not load Calendly meetings';
      setCalendlyError(message);
      setCalendlyMeetings([]);
      toast.error(message);
    } finally {
      setCalendlyLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!connection?.email_address) return;
    void loadCalendlyForConnection(connection.email_address);
  }, [connection?.id, connection?.email_address, loadCalendlyForConnection]);

  const handleManualEmbeddingIndex = useCallback(async () => {
    if (!connection) return;
    setEmbeddingIndexLoading(true);
    try {
      const r = await runConnectionEmbeddingIndex(connection.id);
      if (!r.ok) {
        throw new Error(r.error ?? 'Indexing failed');
      }
      toast.success(
        r.deleted ? 'No ICP or call-me-when text to index — vectors cleared.' : 'Vectors indexed for search.',
      );
      await load();
    } catch (e: unknown) {
      reportUiError('connections', 'embedding_index', e, {
        connection_id: String(connection.id),
      });
      toast.error(e instanceof Error ? e.message : 'Indexing failed');
    } finally {
      setEmbeddingIndexLoading(false);
    }
  }, [connection, load]);

  const loadTranscriptsForConnection = useCallback(async (email: string | null | undefined) => {
    const trimmed = email?.trim() ?? '';
    if (!trimmed) {
      setLinkedTranscripts([]);
      setTranscriptsError(null);
      setTranscriptsLoading(false);
      return;
    }
    setTranscriptsLoading(true);
    setTranscriptsError(null);
    try {
      const rows = await fetchTranscriptsByParticipantEmail(trimmed);
      setLinkedTranscripts(rows);
    } catch (e: unknown) {
      reportUiError('transcripts', 'list_by_email', e, { email: trimmed });
      const message = e instanceof Error ? e.message : 'Could not load transcripts';
      setTranscriptsError(message);
      setLinkedTranscripts([]);
      toast.error(message);
    } finally {
      setTranscriptsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!connection?.email_address?.trim()) {
      setLinkedTranscripts([]);
      setTranscriptsError(null);
      setTranscriptsLoading(false);
      return;
    }
    void loadTranscriptsForConnection(connection.email_address);
  }, [connection?.id, connection?.email_address, loadTranscriptsForConnection]);

  useEffect(() => {
    if (!connection) {
      setBeehiivStatus('idle');
      setBeehiivMatch(null);
      setBeehiivError(null);
      setBeehiivLookupEmail(null);
      beehiivConnectionEmailRef.current = '';
      setBeehiivSubscriberModalOpen(false);
      setBeehiivDetailMatch(null);
      setBeehiivDetailsLoading(false);
      setBeehiivDetailsError(null);
      return;
    }
    const email = connection.email_address?.trim();
    if (!email) {
      setBeehiivStatus('no_email');
      setBeehiivMatch(null);
      setBeehiivError(null);
      setBeehiivLookupEmail(null);
      beehiivConnectionEmailRef.current = '';
      setBeehiivSubscriberModalOpen(false);
      setBeehiivDetailMatch(null);
      setBeehiivDetailsLoading(false);
      setBeehiivDetailsError(null);
      return;
    }
    let cancelled = false;
    beehiivConnectionEmailRef.current = email;
    setBeehiivStatus('loading');
    setBeehiivMatch(null);
    setBeehiivError(null);
    setBeehiivLookupEmail(null);
    setBeehiivSubscriberModalOpen(false);
    setBeehiivDetailMatch(null);
    setBeehiivDetailsLoading(false);
    setBeehiivDetailsError(null);
    void (async () => {
      try {
        const { data, error } = await supabase.functions.invoke<{
          found?: boolean;
          lookupEmail?: string;
          error?: string;
          subscription?: BeehiivEmailLookupSubscription;
          tags?: string[];
          customFields?: BeehiivEmailLookupField[];
        }>('beehiiv-subscriptions', { body: { findSubscriptionByEmail: email } });
        if (cancelled) return;
        if (error) {
          throw new Error(error.message || 'Beehiiv request failed');
        }
        if (data?.error) {
          throw new Error(data.error);
        }
        const lookedUp =
          typeof data?.lookupEmail === 'string' && data.lookupEmail.trim() ? data.lookupEmail.trim() : null;
        setBeehiivLookupEmail(lookedUp);
        if (!data?.found) {
          setBeehiivStatus('not_found');
          return;
        }
        if (data.subscription && typeof data.subscription === 'object') {
          setBeehiivMatch({
            subscription: data.subscription,
            tags: Array.isArray(data.tags) ? data.tags : [],
            customFields: Array.isArray(data.customFields) ? data.customFields : [],
          });
          setBeehiivStatus('found');
        } else {
          setBeehiivStatus('not_found');
        }
      } catch (e: unknown) {
        if (cancelled) return;
        captureEdgeFunctionFailure('beehiiv-subscriptions', 'find_by_email', e, {
          email,
        });
        setBeehiivLookupEmail(null);
        setBeehiivError(e instanceof Error ? e.message : 'Beehiiv lookup failed');
        setBeehiivStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connection?.id, connection?.email_address]);

  useLayoutEffect(() => {
    if (emailInlineEdit) {
      emailInputRef.current?.focus();
      emailInputRef.current?.select();
    }
  }, [emailInlineEdit]);

  useLayoutEffect(() => {
    if (icpInlineEdit) icpTaRef.current?.focus();
  }, [icpInlineEdit]);

  useLayoutEffect(() => {
    if (callWhenInlineN != null) callWhenTaRef.current?.focus();
  }, [callWhenInlineN]);

  useEffect(() => {
    setEmailInlineEdit(false);
    setEmailInlineDraft('');
    setEmailSaving(false);
  }, [connection?.id]);

  const startEmailInlineEdit = useCallback(() => {
    if (!connection) return;
    setEmailInlineDraft(connection.email_address ?? '');
    setEmailInlineEdit(true);
  }, [connection]);

  const cancelEmailInlineEdit = useCallback(() => {
    setEmailInlineEdit(false);
    setEmailInlineDraft(connection?.email_address ?? '');
  }, [connection?.email_address]);

  const commitEmailInlineEdit = useCallback(async () => {
    if (!connection) return;
    const next = normalizeConnectionEmail(emailInlineDraft);
    const prev = normalizeConnectionEmail(connection.email_address ?? '');
    if (!next) {
      toast.error('Email address is required');
      return;
    }
    if (next === prev) {
      setEmailInlineEdit(false);
      return;
    }
    setEmailSaving(true);
    try {
      const { error } = await supabase
        .from('connections')
        .update({
          email_address: next,
          updated_at: new Date().toISOString(),
        })
        .eq('id', connection.id);
      if (error) throw error;
      toast.success('Email updated');
      setEmailInlineEdit(false);
      scheduleConnectionEmbeddingIndex(connection.id);
      void load();
    } catch (e: unknown) {
      reportUiError('connections', 'update_email', e, { connection_id: String(connection.id) });
      const message = e instanceof Error ? e.message : 'Could not save email';
      if (/duplicate|unique|already exists/i.test(message)) {
        toast.error('Another connection already uses that email');
      } else {
        toast.error(message);
      }
    } finally {
      setEmailSaving(false);
    }
  }, [connection, emailInlineDraft, load]);

  const commitIcpInline = useCallback(async () => {
    if (!connection) return;
    const next = icpInlineDraft.trim();
    const prev = (connection.icp ?? '').trim();
    setIcpInlineEdit(false);
    if (next === prev) return;
    const { error } = await supabase
      .from('connections')
      .update({
        icp: next || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', connection.id);
    if (error) {
      reportUiError('connections', 'update_icp', error, { connection_id: String(connection.id) });
      toast.error(error.message);
      return;
    }
    toast.success('ICP saved');
    scheduleConnectionEmbeddingIndex(connection.id);
    void load();
  }, [connection, icpInlineDraft, load]);

  const commitCallWhenInline = useCallback(async () => {
    if (!connection || callWhenInlineN === null) return;
    const n = callWhenInlineN;
    const draft = callWhenInlineDraft;
    const key = `callMeWhen${n}` as 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3';
    const next = draft.trim();
    const prev = (connection[key] ?? '').trim();
    setCallWhenInlineN(null);
    if (next === prev) return;
    const { error } = await supabase
      .from('connections')
      .update({
        [key]: next || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', connection.id);
    if (error) {
      reportUiError('connections', `update_call_me_when_${n}`, error, {
        connection_id: String(connection.id),
      });
      toast.error(error.message);
      return;
    }
    toast.success('Statement saved');
    scheduleConnectionEmbeddingIndex(connection.id);
    void load();
  }, [callWhenInlineDraft, callWhenInlineN, connection, load]);

  const handleEmailSummary = useCallback(() => {
    setEmailSummaryOpen(true);
  }, []);

  const transcriptViewerLinkedConnection: TranscriptViewerLinkedConnection | null = useMemo(
    () =>
      connection
        ? {
            id: connection.id,
            icp: connection.icp ?? null,
            callMeWhen1: connection.callMeWhen1 ?? null,
            callMeWhen2: connection.callMeWhen2 ?? null,
            callMeWhen3: connection.callMeWhen3 ?? null,
          }
        : null,
    [
      connection?.id,
      connection?.icp,
      connection?.callMeWhen1,
      connection?.callMeWhen2,
      connection?.callMeWhen3,
    ]
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto flex flex-col items-center justify-center py-24 gap-3 text-gray-500 dark:text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <p className="text-sm">Loading connection…</p>
      </div>
    );
  }

  if (notFound || !connection) {
    return (
      <div className="max-w-7xl mx-auto">
        <Link
          to="/connections"
          className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to connections
        </Link>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center text-gray-600 dark:text-gray-400">
          Connection not found.
        </div>
      </div>
    );
  }

  const callWhen: { n: 1 | 2 | 3; text: string | null | undefined }[] = [
    { n: 1, text: connection.callMeWhen1 },
    { n: 2, text: connection.callMeWhen2 },
    { n: 3, text: connection.callMeWhen3 },
  ];
  const lastContactRelative = lastContactDaysAgoLabel(connection.last_contact_at);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <Link
        to="/connections"
        className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to connections
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <Users className="w-7 h-7" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 gap-y-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{connection.full_name}</h1>
              {connection.role?.trim() ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-100 shrink-0">
                  {connection.role.trim()}
                </span>
              ) : null}
              {lastContactRelative ? (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 shrink-0"
                  title={formatWhen(connection.last_contact_at)}
                >
                  Last contact {lastContactRelative}
                </span>
              ) : null}
            </div>
            {emailInlineEdit ? (
              <div className="mt-1 flex flex-wrap items-center gap-2 max-w-xl">
                <input
                  ref={emailInputRef}
                  type="email"
                  value={emailInlineDraft}
                  onChange={(e) => setEmailInlineDraft(e.target.value)}
                  disabled={emailSaving}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitEmailInlineEdit();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEmailInlineEdit();
                    }
                  }}
                  className="flex-1 min-w-[14rem] px-2.5 py-1.5 text-sm border border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  aria-label="Edit email address"
                />
                <button
                  type="button"
                  onClick={() => void commitEmailInlineEdit()}
                  disabled={emailSaving}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {emailSaving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelEmailInlineEdit}
                  disabled={emailSaving}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={startEmailInlineEdit}
                title="Click to edit email"
                className="group mt-0.5 inline-flex max-w-full items-center gap-1.5 rounded-md -mx-1 px-1 py-0.5 text-left text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <span className="break-all">
                  {connection.email_address?.trim() ? (
                    connection.email_address
                  ) : (
                    <span className="italic">Add email</span>
                  )}
                </span>
                <Pencil className="w-3.5 h-3.5 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 self-start shrink-0">
          {connection.linkedin_url?.trim() ? (
            <a
              href={connection.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-[#0A66C2] hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-[#70b6ff] transition-colors"
              aria-label="Open LinkedIn profile"
              title="LinkedIn profile"
            >
              <Linkedin className="w-5 h-5" strokeWidth={1.75} />
            </a>
          ) : null}
          <button
            type="button"
            onClick={handleEmailSummary}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
            title="Send summary via Gmail"
          >
            <Mail className="w-4 h-4 mr-2 shrink-0" />
            Email Summary
          </button>
          <button
            type="button"
            onClick={() => setAiResourceMockOpen(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/50 text-violet-900 dark:text-violet-100 hover:bg-violet-100 dark:hover:bg-violet-900/40"
          >
            <Sparkles className="w-4 h-4 mr-2 shrink-0" />
            Resource AI
          </button>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch lg:gap-6">
        <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2.5 text-sm text-gray-700 dark:text-gray-200">
            <Database className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" />
            <div className="min-w-0">
              <p className="font-medium text-gray-900 dark:text-white">AI search vectors</p>
              <p className="mt-0.5 text-gray-600 dark:text-gray-400">
                Status:{' '}
                <span
                  className={
                    normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'ready'
                      ? 'font-medium text-emerald-600 dark:text-emerald-400'
                      : normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'error'
                        ? 'font-medium text-red-600 dark:text-red-400'
                        : normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'pending'
                          ? 'font-medium text-amber-600 dark:text-amber-400'
                          : ''
                  }
                >
                  {connectionEmbeddingsStatusLabel(connection.connection_embeddings_status)}
                </span>
                {connection.connection_embeddings_indexed_at &&
                (normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'ready' ||
                  normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'error') ? (
                  <span className="text-gray-500 dark:text-gray-500">
                    {' '}
                    ·{' '}
                    {normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'error'
                      ? 'Last successful build '
                      : 'Last built '}
                    {formatWhen(connection.connection_embeddings_indexed_at)}
                  </span>
                ) : null}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                ICP and all three &quot;call me when&quot; fields are embedded for the connections list AI filter.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void handleManualEmbeddingIndex()}
            disabled={embeddingIndexLoading}
            className="mt-3 inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 sm:mt-0"
          >
            {embeddingIndexLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {normalizeConnectionEmbeddingsStatus(connection.connection_embeddings_status) === 'ready'
              ? 'Re-index vectors'
              : 'Build vectors'}
          </button>
        </div>

        <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200/90 bg-white px-3 py-2.5 dark:border-gray-700/90 dark:bg-gray-800">
          {beehiivStatus === 'found' && beehiivMatch ? (
            <button
              type="button"
              onClick={() => openBeehiivSubscriberModal(beehiivMatch.subscription.id)}
              title="Open full beehiiv subscriber"
              className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 rounded-md text-left text-sm text-gray-700 transition hover:bg-emerald-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-gray-200 dark:hover:bg-emerald-950/25 -mx-1 -my-0.5 px-1 py-0.5"
            >
              <Newspaper className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 dark:text-white">Beehiiv subscriber</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  <span className="text-gray-500 dark:text-gray-400">Tier </span>
                  {beehiivMatch.subscription.subscriptionTier || '—'}
                </p>
                <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                  <span className="text-gray-500 dark:text-gray-400">Subscribed </span>
                  {formatBeehiivUnixCreated(beehiivMatch.subscription.created)}
                </p>
              </div>
            </button>
          ) : (
            <div className="flex min-w-0 flex-1 items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <Newspaper className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 dark:text-white">Beehiiv subscriber</p>
                {beehiivStatus === 'loading' || beehiivStatus === 'idle' ? (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-emerald-600" />
                    Checking…
                  </div>
                ) : beehiivStatus === 'no_email' ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Add an email on this connection to look up beehiiv.
                  </p>
                ) : beehiivStatus === 'error' ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {beehiivError ?? 'Could not reach beehiiv.'}
                  </p>
                ) : beehiivStatus === 'not_found' ? (
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    <p>
                      Not in beehiiv for{' '}
                      <span className="font-mono text-gray-800 dark:text-gray-200">{connection.email_address}</span>
                    </p>
                    {beehiivLookupEmail ? (
                      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-500">
                        Queried: <span className="font-mono">{beehiivLookupEmail}</span>
                        {beehiivLookupEmail !== connection.email_address?.trim() ? ' (normalized)' : null}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setInviteNewsletterOpen(true)}
                      className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-600/10 px-2 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-600/15 dark:text-emerald-200 dark:hover:bg-emerald-900/40 sm:w-auto sm:justify-start"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      Draft invite email
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-stretch">
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col min-w-0">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-700/40">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">ICP</h2>
          </div>
          <div className="px-5 py-5 flex-1 min-h-[4rem]">
            {icpInlineEdit ? (
              <textarea
                ref={icpTaRef}
                value={icpInlineDraft}
                onChange={e => setIcpInlineDraft(e.target.value)}
                onBlur={() => {
                  if (skipIcpBlurSave.current) {
                    skipIcpBlurSave.current = false;
                    return;
                  }
                  void commitIcpInline();
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    skipIcpBlurSave.current = true;
                    setIcpInlineEdit(false);
                    setIcpInlineDraft(connection.icp ?? '');
                  }
                }}
                rows={6}
                className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[6rem]"
                aria-label="Edit ICP"
              />
            ) : (
              <div
                role="button"
                tabIndex={0}
                title="Double-click to edit"
                className="rounded-lg -m-1 p-2 cursor-text hover:bg-gray-50 dark:hover:bg-gray-700/40 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                onDoubleClick={() => {
                  setIcpInlineDraft(connection.icp ?? '');
                  setIcpInlineEdit(true);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIcpInlineDraft(connection.icp ?? '');
                    setIcpInlineEdit(true);
                  }
                }}
              >
                {connection.icp?.trim() ? (
                  <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap leading-relaxed">
                    {connection.icp.trim()}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">No ICP on file.</p>
                )}
              </div>
            )}
          </div>
          <div className="px-5 py-3 bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-6 gap-y-1 mt-auto">
            <span>ID {connection.id}</span>
            <span>Created {formatWhen(connection.created_at)}</span>
            {connection.updated_at ? <span>Updated {formatWhen(connection.updated_at)}</span> : null}
          </div>
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col min-w-0">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-700/40 flex items-start gap-3">
            <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" strokeWidth={1.75} />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Call Me When Statement</h2>
          </div>
          <div className="px-5 py-5 flex-1">
            <ol className="space-y-5 list-none m-0 p-0">
              {callWhen.map(({ n, text }) => {
                const defined = Boolean(text?.trim());
                const editing = callWhenInlineN === n;
                return (
                  <li key={n} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {n}
                    </span>
                    <div className="min-w-0 flex-1">
                      {editing ? (
                        <textarea
                          ref={callWhenTaRef}
                          value={callWhenInlineDraft}
                          onChange={e => setCallWhenInlineDraft(e.target.value)}
                          onBlur={() => {
                            if (skipCallWhenBlurSave.current) {
                              skipCallWhenBlurSave.current = false;
                              return;
                            }
                            void commitCallWhenInline();
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              skipCallWhenBlurSave.current = true;
                              setCallWhenInlineN(null);
                              const k = `callMeWhen${n}` as 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3';
                              setCallWhenInlineDraft(connection[k] ?? '');
                            }
                          }}
                          rows={4}
                          className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[4.5rem]"
                          aria-label={`Edit statement ${n}`}
                        />
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          title="Double-click to edit"
                          className="rounded-lg -m-1 p-2 cursor-text hover:bg-gray-50 dark:hover:bg-gray-700/40 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          onDoubleClick={() => {
                            setCallWhenInlineN(n);
                            setCallWhenInlineDraft(text?.trim() ? (text ?? '') : '');
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setCallWhenInlineN(n);
                              setCallWhenInlineDraft(text?.trim() ? (text ?? '') : '');
                            }
                          }}
                        >
                          <p
                            className={`text-sm whitespace-pre-wrap leading-relaxed ${
                              defined
                                ? 'text-gray-900 dark:text-gray-100'
                                : 'text-gray-500 dark:text-gray-400 italic'
                            }`}
                          >
                            {defined ? (text ?? '').trim() : 'nothing defined'}
                          </p>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-stretch">
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col min-w-0">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-violet-50/90 dark:bg-violet-950/30 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <FileText className="w-5 h-5 text-violet-700 dark:text-violet-400 shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Transcripts</h2>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Rows where participants include{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300 break-all">
                    {connection.email_address}
                  </span>
                  .
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadTranscriptsForConnection(connection.email_address)}
              disabled={transcriptsLoading || !connection.email_address?.trim()}
              className="inline-flex shrink-0 items-center justify-center p-2 rounded-lg border border-violet-200 dark:border-violet-800 text-violet-800 dark:text-violet-200 bg-white/80 dark:bg-gray-800 hover:bg-violet-100/80 dark:hover:bg-violet-950/50 disabled:opacity-50"
              aria-label="Refresh transcripts"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${transcriptsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {transcriptsError ? (
            <div className="px-5 py-6 flex-1 flex flex-col items-center justify-center gap-3 text-center">
              <p className="text-sm text-red-700 dark:text-red-300">{transcriptsError}</p>
              <button
                type="button"
                onClick={() => void loadTranscriptsForConnection(connection.email_address)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white"
              >
                Try again
              </button>
            </div>
          ) : transcriptsLoading ? (
            <div className="flex flex-col items-center justify-center flex-1 py-12 gap-2 text-gray-500 dark:text-gray-400">
              <RefreshCw className="w-7 h-7 animate-spin text-violet-500" />
              <p className="text-sm">Loading transcripts…</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 flex-1 m-0 p-0 list-none">
              {linkedTranscripts.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No transcripts list this contact in participants.
                </li>
              ) : (
                linkedTranscripts.map(row => (
                  <li key={row.id} className="border-0 p-0">
                    <button
                      type="button"
                      onClick={() => setViewerTranscript(row)}
                      className="w-full px-5 py-4 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-inset"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white min-w-0">{row.title}</p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Search index
                          </span>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              row.transcript_chunks_status === 'ready'
                                ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                                : row.transcript_chunks_status === 'pending'
                                  ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                                  : row.transcript_chunks_status === 'error'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {transcriptChunksStatusLabel(row.transcript_chunks_status)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatWhen(row.meeting_date)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-snug line-clamp-2">
                        {transcriptPreview(row)}
                      </p>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
          <div className="px-5 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Your transcripts
          </div>
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col min-w-0">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-sky-50/90 dark:bg-sky-950/30 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <CalendarClock className="w-5 h-5 text-sky-700 dark:text-sky-400 shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Calendly meetings</h2>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Invitees matching{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300 break-all">
                    {connection.email_address}
                  </span>{' '}
                  in your Calendly account.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadCalendlyForConnection(connection.email_address)}
              disabled={calendlyLoading || !connection.email_address?.trim()}
              className="inline-flex shrink-0 items-center justify-center p-2 rounded-lg border border-sky-200 dark:border-sky-800 text-sky-800 dark:text-sky-200 bg-white/80 dark:bg-gray-800 hover:bg-sky-100/80 dark:hover:bg-sky-950/50 disabled:opacity-50"
              aria-label="Refresh Calendly meetings"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${calendlyLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {calendlyError ? (
            <div className="px-5 py-6 flex-1 flex flex-col items-center justify-center gap-3 text-center">
              <p className="text-sm text-red-700 dark:text-red-300">{calendlyError}</p>
              <button
                type="button"
                onClick={() => void loadCalendlyForConnection(connection.email_address)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white"
              >
                Try again
              </button>
            </div>
          ) : calendlyLoading ? (
            <div className="flex flex-col items-center justify-center flex-1 py-12 gap-2 text-gray-500 dark:text-gray-400">
              <RefreshCw className="w-7 h-7 animate-spin text-sky-500" />
              <p className="text-sm">Loading Calendly…</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 flex-1 m-0 p-0 list-none">
              {calendlyMeetings.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No Calendly meetings found for this email.
                </li>
              ) : (
                calendlyMeetings.map(row => (
                  <li
                    key={row.id}
                    className="px-5 py-4 hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-2 justify-between gap-y-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{row.eventName}</p>
                      <span
                        className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${calendlyMeetingStatusClass(
                          row.meetingStatus
                        )}`}
                      >
                        {calendlyStatusLabel(row.meetingStatus)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatWhen(row.lastContactAt)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                      Guest: {row.email}
                    </p>
                  </li>
                ))
              )}
            </ul>
          )}
          <div className="px-5 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Live Calendly data
          </div>
        </section>
      </div>

      <TranscriptViewerModal
        isOpen={viewerTranscript != null}
        onClose={() => setViewerTranscript(null)}
        transcript={viewerTranscript}
        linkedConnection={transcriptViewerLinkedConnection}
        onLinkedConnectionSaved={patch => {
          setConnection(prev => {
            if (!prev) return prev;
            const now = new Date().toISOString();
            return {
              ...prev,
              ...patch,
              updated_at: now,
              connection_embeddings_status: 'pending',
              connection_embeddings_indexed_at: null,
            };
          });
        }}
        onIndexSuccess={() => {
          setViewerTranscript(prev => (prev ? { ...prev, transcript_chunks_status: 'ready' } : null));
          void loadTranscriptsForConnection(connection.email_address);
        }}
      />

      <ConnectionAiResourceMock
        isOpen={aiResourceMockOpen}
        onClose={() => setAiResourceMockOpen(false)}
        connection={connection}
      />

      <ConnectionModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        connection={connection}
        onSuccess={() => {
          void load();
          setEditOpen(false);
        }}
      />

      {connection && (
        <EmailSummaryModal
          isOpen={emailSummaryOpen}
          onClose={() => setEmailSummaryOpen(false)}
          connection={connection}
        />
      )}

      <BeehiivSubscriberModal
        isOpen={beehiivSubscriberModalOpen}
        onClose={() => {
          setBeehiivSubscriberModalOpen(false);
          setBeehiivDetailMatch(null);
          setBeehiivDetailsError(null);
        }}
        loading={beehiivDetailsLoading}
        error={beehiivDetailsError}
        detail={beehiivDetailMatch}
      />

      {connection?.email_address?.trim() ? (
        <InviteToNewsletterModal
          isOpen={inviteNewsletterOpen}
          onClose={() => setInviteNewsletterOpen(false)}
          toEmail={connection.email_address}
          recipientName={connection.full_name}
        />
      ) : null}
    </div>
  );
};

export default ConnectionDetailView;
